import { Component } from '@theme/component';

class AnnouncementRotator extends Component {
  rotatorSpeed = 5000;
  requiredRefs = ['rotatorItem'];

  connectedCallback() {
    super.connectedCallback();
    this.rotatorSpeed = this.dataset.rotatorSpeed ? parseInt(this.dataset.rotatorSpeed) : 5000;

    const announcements = this.refs['rotatorItem'];

    if (announcements && announcements.length > 0) {
      this.setupAutoScroll(announcements);
    }

    this.addEventListener('mouseenter', this.pause);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('focusin', this.pause);
    this.addEventListener('focusout', this.resume);
  }

  pause = () => {
    clearInterval(this.autoScrollInterval);
  };

  resume = () => {
    if (!this._items || this._items.length <= 1) return;

    clearInterval(this.autoScrollInterval);
    this.autoScrollInterval = setInterval(() => {
      const nextIndex = (this._currentIndex + 1) % this._items.length;
      this.showItem(nextIndex, true);
      this._currentIndex = nextIndex;
    }, this.rotatorSpeed);
  };

  showItem(index, animate = false) {
    const fromIndex = this._currentIndex;
    const canAnimate =
      animate &&
      this.dataset.animate === 'true' &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
      this._items &&
      this._items.length > 1 &&
      fromIndex !== index &&
      this._items[fromIndex] &&
      this._items[index];

    if (!canAnimate) {
      this.finishAnimation();
      this.applyInstant(index);
      return;
    }

    this.animateFromTo(fromIndex, index);
  }

  applyInstant(index) {
    this._items.forEach((item, i) => {
      item.style.transition = '';
      item.style.transform = '';
      item.classList.remove('show', 'hide');
      if (i === index) {
        item.classList.add('show');
        item.setAttribute('aria-hidden', 'false');
      } else {
        item.classList.add('hide');
        item.setAttribute('aria-hidden', 'true');
      }
    });
  }

  animateFromTo(fromIndex, toIndex) {
    this.finishAnimation();

    const current = this._items[fromIndex];
    const next = this._items[toIndex];
    const distance = this.offsetWidth;

    if (!current || !next || !distance) {
      this.applyInstant(toIndex);
      return;
    }

    const direction = this.dataset.direction === 'left_to_right' ? -1 : 1;
    const shift = direction * distance;

    next.classList.remove('hide');
    next.style.transition = 'none';
    current.style.transition = 'none';
    next.style.transform = `translate3d(${shift}px, 0, 0)`;
    current.style.transform = 'translate3d(0, 0, 0)';

    void next.offsetWidth;

    next.style.transition = 'transform 0.3s ease';
    current.style.transition = 'transform 0.3s ease';
    next.style.transform = 'translate3d(0, 0, 0)';
    current.style.transform = `translate3d(${-shift}px, 0, 0)`;

    current.classList.remove('show');
    next.classList.add('show');
    current.setAttribute('aria-hidden', 'true');
    next.setAttribute('aria-hidden', 'false');

    const animation = { current, next };
    this._animation = animation;

    const complete = (event) => {
      if (this._animation !== animation) return;
      if (event && (event.target !== next || event.propertyName !== 'transform')) return;
      next.removeEventListener('transitionend', complete);
      window.clearTimeout(animation.timer);
      this.finishAnimation();
    };

    next.addEventListener('transitionend', complete);
    animation.timer = window.setTimeout(() => complete(), 400);
  }

  finishAnimation() {
    const animation = this._animation;
    if (!animation) return;

    this._animation = null;
    window.clearTimeout(animation.timer);

    const { current, next } = animation;
    current.classList.remove('show');
    current.classList.add('hide');
    current.setAttribute('aria-hidden', 'true');
    current.style.transition = '';
    current.style.transform = '';
    next.classList.remove('hide');
    next.classList.add('show');
    next.setAttribute('aria-hidden', 'false');
    next.style.transition = '';
    next.style.transform = '';
  }

  setupAutoScroll(items) {
    this._items = items;
    this._currentIndex = 0;

    this.showItem(this._currentIndex);

    if (items.length > 1) {
      this.autoScrollInterval = setInterval(() => {
        const nextIndex = (this._currentIndex + 1) % items.length;
        this.showItem(nextIndex, true);
        this._currentIndex = nextIndex;
      }, this.rotatorSpeed);
    }
  }

  disconnectedCallback() {
    clearInterval(this.autoScrollInterval);
    this.finishAnimation();
    this.removeEventListener('mouseenter', this.pause);
    this.removeEventListener('mouseleave', this.resume);
    this.removeEventListener('focusin', this.pause);
    this.removeEventListener('focusout', this.resume);
  }
}

if (!customElements.get('announcement-rotator')) {
  customElements.define('announcement-rotator', AnnouncementRotator);
}
